/**
 * V3 — Journal browser. Filtres, recherche, export Excel.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getJournal, journalExportUrl } from './api';
import { Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';

function fmtCurrency(n) {
  if (n === null || n === undefined || n === 0) return '—';
  return Math.round(Number(n)).toLocaleString('fr-FR');
}
function fmtLitres(n) {
  if (!n) return '—';
  return Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

export default function V3Journal() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getJournal({ from, to, search, page, page_size: 30 });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  return (
    <div className="v3-page" data-testid="v3-journal">
      <div className="v3-page-head">
        <h1>Journal Station ROPS</h1>
        <p>{data.total} entrée{data.total>1?'s':''} · lecture seule</p>
      </div>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} data-testid="filter-from" />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} data-testid="filter-to" />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14} />
            <input type="text" placeholder="Caissier, gérant, date…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} data-testid="filter-search" />
          </div>
        </div>
        <a href={journalExportUrl({ from, to })} className="v3-btn v3-btn-primary" data-testid="btn-export-xlsx">
          <Download size={16}/>
          Exporter Excel
        </a>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table" data-testid="journal-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Caissier</th>
                <th>Gérant</th>
                <th className="num">Litres GO</th>
                <th className="num">Litres SP</th>
                <th className="num">CA Piste</th>
                <th className="num">CA Shop</th>
                <th className="num">Versé banque</th>
                <th className="num">Écart</th>
                <th className="num">Crédits</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((j) => (
                <React.Fragment key={(j.raw_id || j.date) + '-' + j.caissier}>
                  <tr className="v3-row" onClick={() => setExpanded(expanded === j.raw_id ? null : (j.raw_id || j.date))}>
                    <td className="mono">{j.date}</td>
                    <td>{j.caissier || '—'}</td>
                    <td>{j.gerant || '—'}</td>
                    <td className="num">{fmtLitres(j.litres_go)}</td>
                    <td className="num">{fmtLitres(j.litres_sp)}</td>
                    <td className="num strong">{fmtCurrency(j.ca_piste)}</td>
                    <td className="num">{fmtCurrency(j.ca_shop)}</td>
                    <td className="num">{fmtCurrency(j.versement_banque)}</td>
                    <td className={`num ${j.ecart < 0 ? 'negative' : ''}`}>{fmtCurrency(j.ecart)}</td>
                    <td className="num">{fmtCurrency(j.total_credits)}</td>
                    <td className="action">{expanded === (j.raw_id || j.date) ? '▼' : '▸'}</td>
                  </tr>
                  {expanded === (j.raw_id || j.date) && (
                    <tr className="v3-row-detail">
                      <td colSpan={11}>
                        <div className="v3-detail-grid">
                          <DetailItem label="CA Gasoil" value={fmtCurrency(j.ca_go)} />
                          <DetailItem label="CA Super" value={fmtCurrency(j.ca_sp)} />
                          <DetailItem label="CA Total" value={fmtCurrency(j.ca_total)} />
                          <DetailItem label="Caisse théorique" value={fmtCurrency(j.caisse_theorique)} />
                          <DetailItem label="Total manquants pompistes" value={fmtCurrency(j.total_manquants)} />
                        </div>
                        {j.credits && Object.keys(j.credits).length > 0 && (
                          <div className="v3-credits-detail">
                            <h4>Crédits clients</h4>
                            <div className="v3-credits-list">
                              {Object.entries(j.credits).map(([name, amt]) => (
                                <div key={name} className="v3-credit-item">
                                  <span>{name}</span>
                                  <strong>{fmtCurrency(amt)}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={11} className="v3-empty">Aucune entrée pour ces filtres.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={11} className="v3-empty">Chargement…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} data-testid="btn-prev">
              <ChevronLeft size={16}/>Préc.
            </button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages} data-testid="btn-next">
              Suiv.<ChevronRight size={16}/>
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="v3-detail-item">
      <div className="v3-detail-label">{label}</div>
      <div className="v3-detail-value">{value}</div>
    </div>
  );
}
